/**
 * Live Pulse Hook - UNIFIED REALTIME + TRUE TIMELINE
 *
 * Reconstructs a full activity timeline from internal_notes (today + yesterday),
 * then appends new events from the unified OrdersRealtimeProvider in real time.
 *
 * SINGLE SOURCE OF TRUTH: Reads ONLY from internal_notes.
 *
 * Design decisions:
 * - Initial load parses ALL meaningful log lines per order (not just the latest)
 * - Fetch window includes today + yesterday for "what happened overnight?"
 * - Events are deduped by canonical type + timestamp, not display text
 * - Acknowledged events are filtered as noise
 * - Event IDs include line index to prevent collision
 */

import { useState, useEffect, useRef, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useRealtimeContext } from "@/providers/RealtimeContext";
import { parseLogRole, type DeliveryLogRole } from "@/lib/deliveryLogFormat";
import { playAlertSound, playCriticalAlertSound } from "@/lib/notificationSound";
import {
  classifyEvent,
  isNoiseEvent,
  parseTimestampFromLogLine,
  buildEventDedupeKey,
  type CanonicalEventType,
} from "@/lib/activityFeedUtils";
import { startOfDay, subDays } from "date-fns";
import { toZonedTime } from "date-fns-tz";

export type PulseEventType = "manager_action" | "courier_action" | "system_action" | "critical";

export interface PulseEvent {
  id: string;
  orderId: string;
  orderNumber: string;
  customerName: string;
  type: PulseEventType;
  role: "manager" | "courier" | "system";
  message: string;
  canonicalType: CanonicalEventType;
  timestamp: Date;
  isCritical: boolean;
  isNew: boolean;
  isDelivered: boolean;
  /** Deterministic dedupe key: orderId + canonicalType + minute-rounded timestamp */
  dedupeKey: string;
}

const MAX_EVENTS = 50;

/**
 * Detect if a log line represents a critical event (needs manager attention)
 */
const isCriticalLogLine = (line: string): boolean => {
  const lowerLine = line.toLowerCase();
  return (
    line.includes("⚠️") ||
    lowerLine.includes("can't take") ||
    lowerLine.includes("delivery failed") ||
    lowerLine.includes("ვერ ვიღებ") ||
    lowerLine.includes("ვერ მიიტანტე") ||
    lowerLine.includes("cancelled") ||
    lowerLine.includes("გაუქმებულია")
  );
};

/**
 * Detect if a log line represents a delivered/success event
 */
const isDeliveredLogLine = (line: string): boolean => {
  const lowerLine = line.toLowerCase();
  return lowerLine.includes("delivered") || lowerLine.includes("მიტანილია") || lowerLine.includes("ჩაბარდა");
};

/**
 * Check if a message is just noise (role-only, empty)
 */
const isNoiseMessage = (message: string): boolean => {
  const trimmed = message.trim().toLowerCase();

  if (["courier", "manager", "system", "კურიერი", "მენეჯერი", "სისტემა"].includes(trimmed)) {
    return true;
  }

  return trimmed.length < 3;
};

/**
 * Extract the human-readable message from a log line
 */
const extractMessageFromLogLine = (line: string): string => {
  const parts = line.split(" · ");
  // 4-part format: [role] · date · time · message (timestamp contains " · ")
  if (parts.length >= 4) {
    return parts.slice(3).join(" · ").trim();
  }
  // 3-part format: [role] · time · message
  if (parts.length >= 3) {
    return parts.slice(2).join(" · ").trim();
  }

  return line
    // eslint-disable-next-line no-misleading-character-class
    .replace(/^[👤🚚⚙️🧑‍💼🤖📥✅⚠️❌]\s*/gu, "")
    .replace(/\[(manager|courier|system)\]\s*/gi, "")
    .replace(/^\d{1,2}:\d{2}\s*[·]?\s*/g, "")
    .trim();
};

/**
 * Parse ALL meaningful log entries from internal_notes.
 * Returns an array of PulseEvents — one per meaningful log line.
 */
function parseAllLogEntries(
  order: {
    id: string;
    order_number: string | null;
    customer_name: string;
    internal_notes: string | null;
    updated_at: string;
  },
  isNew: boolean = false,
): PulseEvent[] {
  if (!order.internal_notes) return [];

  const lines = order.internal_notes.trim().split("\n").filter(Boolean);
  const events: PulseEvent[] = [];

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const role = parseLogRole(line);
    if (!role) continue;

    const message = extractMessageFromLogLine(line);
    if (isNoiseMessage(message)) continue;

    const canonicalType = classifyEvent(message);
    if (isNoiseEvent(canonicalType)) continue;

    // Parse timestamp from log line, fall back to order.updated_at
    const parsedTs = parseTimestampFromLogLine(line) || new Date(order.updated_at);
    const isCritical = isCriticalLogLine(line);
    const isDelivered = isDeliveredLogLine(line);

    const dedupeKey = buildEventDedupeKey(order.id, canonicalType, parsedTs);

    const eventType: PulseEventType = isCritical
      ? "critical"
      : role === "courier"
        ? "courier_action"
        : role === "system"
          ? "system_action"
          : "manager_action";

    events.push({
      id: `${order.id}-${i}-${parsedTs.getTime()}`,
      orderId: order.id,
      orderNumber: order.order_number || String(parseInt(order.id.slice(-4), 16)),
      customerName: order.customer_name,
      type: eventType,
      role: role as "manager" | "courier" | "system",
      message: message.slice(0, 60),
      canonicalType,
      timestamp: parsedTs,
      isCritical,
      isNew,
      isDelivered,
      dedupeKey,
    });
  }

  return events;
}

/**
 * Parse only the LATEST meaningful log entry (used for realtime updates).
 * When `timestampOverride` is provided (realtime events), it is used instead
 * of the minute-rounded parsed timestamp to ensure precise unread detection.
 */
function parseLatestLogEntry(
  order: {
    id: string;
    order_number: string | null;
    customer_name: string;
    internal_notes: string | null;
    updated_at: string;
  },
  isNew: boolean = false,
  timestampOverride?: Date,
): PulseEvent | null {
  if (!order.internal_notes) return null;

  const lines = order.internal_notes.trim().split("\n").filter(Boolean);

  for (let i = lines.length - 1; i >= 0; i--) {
    const line = lines[i];
    const role = parseLogRole(line);
    if (!role) continue;

    const message = extractMessageFromLogLine(line);
    if (isNoiseMessage(message)) continue;

    const canonicalType = classifyEvent(message);
    if (isNoiseEvent(canonicalType)) continue;

    // Use override for realtime events (precise ms), parsed for initial load
    const parsedTs = timestampOverride || parseTimestampFromLogLine(line) || new Date(order.updated_at);
    const isCritical = isCriticalLogLine(line);
    const isDelivered = isDeliveredLogLine(line);

    const dedupeKey = buildEventDedupeKey(order.id, canonicalType, parsedTs);

    const eventType: PulseEventType = isCritical
      ? "critical"
      : role === "courier"
        ? "courier_action"
        : role === "system"
          ? "system_action"
          : "manager_action";

    return {
      id: `${order.id}-${i}-${parsedTs.getTime()}`,
      orderId: order.id,
      orderNumber: order.order_number || String(parseInt(order.id.slice(-4), 16)),
      customerName: order.customer_name,
      type: eventType,
      role: role as "manager" | "courier" | "system",
      message: message.slice(0, 60),
      canonicalType,
      timestamp: parsedTs,
      isCritical,
      isNew,
      isDelivered,
      dedupeKey,
    };
  }

  return null;
}

/**
 * Get the number of log lines in notes
 */
const getLogLineCount = (notes: string | null): number => {
  if (!notes) return 0;
  return notes.trim().split("\n").filter(Boolean).length;
};

interface UseLivePulseOptions {
  enableSound?: boolean;
}

export function useLivePulse(options: UseLivePulseOptions = {}) {
  const { enableSound = false } = options;
  const { user } = useAuth();
  const { connectionState, subscribeToChanges } = useRealtimeContext();
  const [events, setEvents] = useState<PulseEvent[]>([]);

  // Mutable ref for sound preference — avoids resubscription when toggled
  const enableSoundRef = useRef(enableSound);
  enableSoundRef.current = enableSound;

  // Derive connection status from unified provider
  const isConnected = connectionState === 'live';

  // Track previous notes length per order to detect new log entries
  const previousNotesLengthRef = useRef<Map<string, number>>(new Map());

  // Track dedupe keys to prevent duplicates between initial load and realtime
  const dedupeKeysRef = useRef<Set<string>>(new Set());

  const mergeAndSort = useCallback(
    (eventList: PulseEvent[]): PulseEvent[] => {
      // Dedupe by dedupeKey
      const seen = new Set<string>();
      const unique: PulseEvent[] = [];
      for (const evt of eventList) {
        if (!seen.has(evt.dedupeKey)) {
          seen.add(evt.dedupeKey);
          unique.push(evt);
        }
      }
      return unique
        .sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime())
        .slice(0, MAX_EVENTS);
    },
    [],
  );

  // Fetch initial events (today + yesterday order activity from internal_notes)
  const fetchInitialEvents = useCallback(async () => {
    if (!user?.id) return;

    const tbilisiNow = toZonedTime(new Date(), "Asia/Tbilisi");
    const yesterdayStart = startOfDay(subDays(tbilisiNow, 1));

    const { data: orders, error } = await supabase
      .from("orders")
      .select("id, order_number, customer_name, internal_notes, updated_at")
      .eq("user_id", user.id)
      .eq("is_archived", false)
      .gte("updated_at", yesterdayStart.toISOString())
      .not("internal_notes", "is", null)
      .order("updated_at", { ascending: false })
      .limit(200);

    if (error) {
      console.error("[LivePulse] Failed to fetch initial events:", error);
      return;
    }

    if (!orders) return;

    const allEvents: PulseEvent[] = [];
    previousNotesLengthRef.current.clear();
    dedupeKeysRef.current.clear();

    for (const order of orders) {
      const notesLength = getLogLineCount(order.internal_notes);
      previousNotesLengthRef.current.set(order.id, notesLength);

      // Parse ALL log lines for true timeline
      const orderEvents = parseAllLogEntries(order, false);
      allEvents.push(...orderEvents);
    }

    const sorted = mergeAndSort(allEvents);
    // Populate dedupe keys from initial load
    for (const evt of sorted) {
      dedupeKeysRef.current.add(evt.dedupeKey);
    }
    setEvents(sorted);
  }, [user?.id, mergeAndSort]);

  // Fetch initial events on mount
  useEffect(() => {
    if (!user?.id) return;
    fetchInitialEvents();
  }, [user?.id, fetchInitialEvents]);

  // Subscribe to unified realtime events
  useEffect(() => {
    if (!user?.id) return;

    const unsubscribe = subscribeToChanges((payload) => {
      const newOrder = payload.newOrder as any;
      if (!newOrder) return;

      const now = new Date();
      const currentNotesLength = getLogLineCount(newOrder.internal_notes);
      const previousNotesLength = previousNotesLengthRef.current.get(newOrder.id) || 0;

      // Handle INSERT - new order
      if (payload.eventType === "INSERT") {
        previousNotesLengthRef.current.set(newOrder.id, currentNotesLength);

        const event = parseLatestLogEntry(
          {
            id: newOrder.id,
            order_number: newOrder.order_number,
            customer_name: newOrder.customer_name,
            internal_notes: newOrder.internal_notes,
            updated_at: now.toISOString(),
          },
          true,
          now, // Use precise timestamp for realtime events (not minute-rounded)
        );

        if (event && !dedupeKeysRef.current.has(event.dedupeKey)) {
          dedupeKeysRef.current.add(event.dedupeKey);
          if (enableSoundRef.current) {
            if (event.isCritical) {
              playCriticalAlertSound();
            } else {
              playAlertSound();
            }
          }
          setEvents((prev) => mergeAndSort([event, ...prev]));
        }
        return;
      }

      // Handle UPDATE - only if new log lines were added
      if (payload.eventType === "UPDATE") {
        const hasNewLogEntry = currentNotesLength > previousNotesLength;
        previousNotesLengthRef.current.set(newOrder.id, currentNotesLength);

        if (hasNewLogEntry) {
          const event = parseLatestLogEntry(
            {
              id: newOrder.id,
              order_number: newOrder.order_number,
              customer_name: newOrder.customer_name,
              internal_notes: newOrder.internal_notes,
              updated_at: now.toISOString(),
            },
            true,
            now, // Use precise timestamp for realtime events (not minute-rounded)
          );

          if (event && !dedupeKeysRef.current.has(event.dedupeKey)) {
            dedupeKeysRef.current.add(event.dedupeKey);
            if (enableSoundRef.current) {
              if (event.isCritical) {
                playCriticalAlertSound();
              } else {
                playAlertSound();
              }
            }
            setEvents((prev) => mergeAndSort([event, ...prev]));
          }
        }
      }
    });

    return unsubscribe;
  }, [user?.id, subscribeToChanges, mergeAndSort]);

  return { events, isConnected };
}
